// Record models for each area's home page: the records people navigate, and how each relates to its parent.
// This is the ontology seen from one area. Each node opens a page: a courageMFG screen (`to`, or the app it
// belongs to while it's planned), a Business Central page (`bc` = BC page ID), or nothing.
import type { Status } from "./suite";

export type ModelNode = {
  name: string;
  side: "courage" | "bc";
  rel?: string; // how it relates to its parent, read as "<parent> <rel> <this>"
  to?: string; // courageMFG route
  app?: string; // courageMFG app id (planned records open the app page)
  bc?: number; // Business Central page ID
  status?: Status; // for courageMFG records not yet built
  note?: string;
  children?: ModelNode[];
};

const bc = (name: string, page: number, rel?: string, children?: ModelNode[], note?: string): ModelNode => ({ name, side: "bc", bc: page, rel, children, note });
const cm = (name: string, app: string, status: Status, rel?: string, children?: ModelNode[], note?: string, to?: string): ModelNode =>
  ({ name, side: "courage", app, status, rel, children, note, to });

export const MODELS: Record<string, ModelNode[]> = {
  commercial: [
    cm("Leads", "leads", "r3", undefined, [
      cm("Account", "accounts", "live", "converts to", [
        bc("Customers", 22, "becomes, once qualified", [
          bc("Ship-to addresses", 301, "ships to"),
          bc("Sales orders", 9305, "orders on", [
            bc("Posted sales invoices", 143, "is invoiced as"),
            bc("Lots shipped", 6508, "ships"),
          ]),
          bc("Customer ledger entries", 25, "owes"),
        ], "Convert account to customer creates it in BC from the contact, once credit and terms are approved. One account can hold several BC customers (sell-to, bill-to, or one per BC company)."),
        cm("Opportunities", "crm", "live", "is pursued in", [
          cm("Quotes", "quotes", "live", "has", [
            bc("Sales quotes", 9300, "is issued as", [bc("Sales orders", 9305, "when won, converts to")], "Made out to the contact until the account is a customer"),
          ], "Revisions, costed from PLM BOMs and routings"),
        ], "Created when the lead converts, or added later"),
        bc("Contacts", 5052, "has", undefined, "People at the account. The lead's contact is created in BC at conversion, before any customer exists."),
        cm("Service cases", "cases", "r3", "is supported in", [
          cm("Complaints", "cases", "r3", "has", [
            bc("Lots", 6508, "traced to"),
            cm("CAPA", "capa", "live", "escalates to", undefined, undefined, "/capas"),
          ], "A sub-record of the case, when the problem is a quality complaint"),
          cm("Returns (RMA)", "cases", "r3", "has", [bc("Sales return orders", 9304, "is received on")]),
        ]),
      ], "A prospect until qualified"),
    ]),
  ],
  engineering: [
    cm("New item requests", "new-items", "r2", undefined, [
      bc("Items", 31, "creates, once approved", [
        cm("Item revisions", "new-items", "r2", "has", [cm("Drawings and specs", "doc-control", "r1", "is defined by")]),
        bc("Item variants", 5401, "has"),
        bc("Production BOMs", 99000787, "is built from", [
          bc("Component items", 31, "consumes", undefined, "BOM lines: each component, quantity per and scrap %"),
        ], "Certified versions only are used for production"),
        bc("Routings", 99000764, "is made on", [
          bc("Work centers", 99000755, "each operation runs on", [bc("Machine centers", 99000760, "contains")]),
          cm("Tooling and fixtures", "resources", "future", "each operation needs"),
        ]),
      ]),
    ], "Engineering, purchasing and costing each complete their part"),
    cm("Change requests (ECR)", "eco", "r2", undefined, [
      cm("Change orders (ECO)", "eco", "r2", "is approved as", [
        bc("Production BOMs", 99000787, "releases a new version of"),
        bc("Routings", 99000764, "releases a new version of"),
        cm("Item revisions", "new-items", "r2", "creates"),
        cm("Controlled documents", "doc-control", "r1", "revises"),
      ], "Approval routing with e-signatures; release writes to BC"),
    ], "Raised from a CAPA, an issue or engineering"),
    cm("NPI projects", "npi", "future", undefined, [
      cm("Gates", "npi", "future", "moves through", [cm("Deliverables", "npi", "future", "requires")]),
      cm("New item requests", "new-items", "r2", "creates"),
      cm("First article inspection", "inspection", "future", "closes with"),
    ]),
  ],
  quality: [
    cm("Issues", "issues", "live", undefined, [
      bc("Lots", 6508, "is linked to"), bc("Work centers", 99000755, "happened at"), bc("Production orders", 9326, "occurred on"),
      cm("CAPA", "capa", "live", "escalates to", [
        cm("Containment actions", "capa", "live", "has"),
        cm("Root cause", "capa", "live", "has", undefined, "5 whys, fishbone"),
        cm("Corrective actions", "capa", "live", "has"),
        cm("Effectiveness check", "capa", "live", "is closed by"),
        cm("SCAR", "scar", "r1", "sends", undefined, "When the supplier caused it"),
        cm("Change requests (ECR)", "eco", "r2", "raises"),
        cm("Controlled documents", "doc-control", "r1", "revises"),
      ], undefined, "/capas"),
    ], undefined, "/issues"),
    cm("Nonconformances (NCR)", "ncr", "future", undefined, [
      cm("Holds", "holds", "future", "places", [bc("Lots", 6508, "blocks")]),
      cm("MRB review", "ncr", "future", "is decided in", [
        cm("Disposition", "ncr", "future", "records", [
          bc("Rework production orders", 9326, "rework creates"),
          bc("Purchase return orders", 9311, "return to vendor creates"),
          bc("Item journal (scrap)", 40, "scrap posts in"),
        ], "Use as is, rework, scrap or return"),
      ]),
    ]),
    cm("Inspection plans", "inspection", "future", undefined, [
      cm("Characteristics", "inspection", "future", "checks", undefined, "Dimension, spec and sample size"),
      cm("Inspection results", "inspection", "future", "is recorded as", [
        bc("Lots", 6508, "per"), cm("Issues", "issues", "live", "a failure raises", undefined, undefined, "/issues"),
      ]),
    ], "By item (receiving) or by routing operation (in process)"),
    cm("Gauges", "calibration", "future", undefined, [
      cm("Calibration records", "calibration", "future", "has", undefined, "Certificates attached"),
      cm("Inspection results", "inspection", "future", "measured", undefined, "Out of tolerance shows every lot this gauge checked"),
    ]),
    cm("Audits", "audits", "future", undefined, [
      cm("Findings", "audits", "future", "has", [cm("CAPA", "capa", "live", "escalates to", undefined, undefined, "/capas")]),
    ], "Internal, supplier and customer"),
    cm("FMEAs", "fmea", "future", undefined, [cm("Failure modes", "fmea", "future", "has", [cm("Control plan", "fmea", "future", "is controlled by")])]),
  ],
  supply: [
    bc("Vendors", 27, undefined, [
      bc("Contacts", 5052, "has"),
      cm("Approved supplier status", "supplier-portal", "r1", "is approved as", undefined, "Approved supplier list: what each vendor may supply"),
      bc("Purchase orders", 9307, "is sent", [
        cm("PO acknowledgements", "supplier-portal", "live", "each line is confirmed by", undefined, "Supplier confirms the date, or proposes a new one, in the portal", "/supplier-portal"),
        bc("Posted purchase receipts", 145, "is received on", [bc("Lots received", 6508, "brings in"), cm("Receiving inspection", "inspection", "future", "is checked by")]),
        bc("Posted purchase invoices", 146, "is invoiced as"),
      ]),
      cm("SCARs", "scar", "r1", "answers", [cm("8D response", "scar", "r1", "has")]),
      cm("Scorecards", "scorecards", "r1", "is scored monthly in", undefined, "On-time delivery, PPM, SCAR responsiveness"),
    ]),
    bc("Items", 31, undefined, [
      cm("Demand plan", "demand-planning", "future", "is forecast in", [
        cm("Supply plan", "demand-planning", "future", "drives", [
          bc("Planning worksheet", 99000852, "suggests in", [bc("Purchase orders", 9307, "creates"), bc("Planned production orders", 9323, "creates")]),
          bc("Requisition worksheet", 291, "suggests buys in"),
        ], "Every suggestion pegged to the demand behind it"),
      ]),
      bc("Transfer orders", 5742, "moves between locations by"),
    ]),
    cm("S&OP cycles", "demand-planning", "future", undefined, [cm("Decisions", "demand-planning", "future", "records")], "Monthly demand, supply and capacity review"),
  ],
  production: [
    bc("Production orders", 9326, undefined, [
      bc("Items", 31, "produces"),
      bc("Routings", 99000764, "follows", [bc("Work centers", 99000755, "each operation runs on")]),
      { name: "Components", side: "bc", rel: "consumes", children: [bc("Lots consumed", 6508, "from")], note: "Production order components, picked and flushed; open the order in BC" },
      cm("Schedule", "scheduling", "future", "is scheduled on"),
      cm("Operator jobs", "shop-floor", "future", "is worked as", [
        cm("Work instructions", "doc-control", "r1", "shows"),
        bc("Output journal", 99000823, "reports output and scrap in"),
        bc("Consumption journal", 99000846, "reports consumption in"),
      ]),
      bc("Lots produced", 6508, "outputs"),
      cm("Issues", "issues", "live", "has", undefined, undefined, "/issues"),
    ], "Planned → firm planned → released → finished, in BC"),
    bc("Work centers", 99000755, undefined, [
      bc("Machine centers", 99000760, "contains", [cm("Downtime events", "downtime", "future", "loses time to", [cm("Maintenance work orders", "work-orders", "live", "raises")])]),
      cm("Tier board", "tier-boards", "r1", "huddles on", [cm("Tier actions", "tier-boards", "r1", "has")]),
      cm("Shift handoffs", "shop-floor", "future", "has"),
    ]),
  ],
  inventory: [
    bc("Locations", 15, undefined, [
      bc("Bins", 7303, "has", [bc("Items in bin", 31, "holds")]),
      bc("Warehouse receipts", 7332, "receives on"),
      bc("Warehouse shipments", 7339, "ships on"),
      bc("Transfer orders", 5742, "transfers by"),
    ]),
    bc("Items", 31, undefined, [
      bc("Item ledger entries", 38, "moves are recorded in"),
      bc("Lots", 6508, "is tracked by", [
        cm("Holds", "holds", "future", "is held by", [cm("Nonconformances (NCR)", "ncr", "future", "is decided in")], "Blocks the lot in BC until released"),
      ]),
      cm("Counts", "cycle-counts", "live", "is counted by", [bc("Physical inventory journal", 392, "differences post in")], "Cycle counts by ABC class, full counts and quick counts", "/inventory/counts"),
      bc("Item journal", 40, "is adjusted in"),
    ]),
  ],
  maintenance: [
    cm("Assets", "assets", "live", undefined, [
      bc("Machine centers", 99000760, "is the BC machine center", undefined, "Production capacity"),
      bc("Fixed assets", 5601, "is booked as", undefined, "Book value and depreciation"),
      cm("PM schedules", "pm", "live", "has", [cm("Work orders", "work-orders", "live", "generates")], "By calendar, run hours or cycles"),
      cm("Work orders", "work-orders", "live", "is maintained by", [
        cm("Tasks", "work-orders", "live", "has"),
        cm("Spare parts used", "spares", "live", "uses", [bc("Items", 31, "are BC items")], "Issued from BC inventory"),
        cm("Labor", "work-orders", "live", "records"),
      ], "From a PM, an issue, or downtime"),
      cm("Downtime events", "downtime", "future", "loses time to"),
      cm("Meter readings", "pm", "live", "has"),
    ]),
  ],
  safety: [
    cm("Incidents", "incidents", "future", undefined, [
      cm("Investigation", "incidents", "future", "has", [cm("Corrective actions", "incidents", "future", "results in")]),
      bc("Employees", 5201, "involves"),
      cm("OSHA 300 log entry", "incidents", "future", "is recorded as", undefined, "When recordable"),
    ], "Injuries, near misses and property damage"),
    cm("Safety inspections", "safety-audits", "future", undefined, [cm("Findings", "safety-audits", "future", "has", [cm("Corrective actions", "incidents", "future", "results in")])]),
    cm("Chemicals", "environmental", "future", undefined, [cm("Safety data sheets", "environmental", "future", "has"), bc("Items", 31, "is the BC item")]),
    cm("Permits and waste", "environmental", "future", undefined, [cm("Waste manifests", "environmental", "future", "has")]),
    cm("Visitors and contractors", "visitors", "future", undefined, [cm("Sign-ins", "visitors", "future", "has", [cm("Safety briefing acknowledgement", "visitors", "future", "requires")])]),
  ],
  documents: [
    cm("Controlled documents", "doc-control", "r1", undefined, [
      cm("Revisions", "doc-control", "r1", "has", [
        cm("Approvals", "doc-control", "r1", "is signed off by", undefined, "E-signatures"),
        cm("Acknowledgements", "acknowledgements", "r1", "requires", undefined, "Required readers by role"),
      ], "Draft → review → approved → effective → obsolete"),
      cm("Document change requests", "doc-control", "r1", "is changed by", undefined, "From a CAPA, an ECO, an audit or anyone"),
      bc("Items", 31, "specifies"),
      bc("Work centers", 99000755, "is posted at", undefined, "QR code at the station opens the current revision"),
      cm("Records", "doc-control", "r1", "is filled in as", undefined, "Completed forms and checklists"),
    ], "Procedures, work instructions, specs and forms"),
  ],
  training: [
    bc("Employees", 5201, undefined, [
      cm("Qualifications", "skills-matrix", "future", "holds", [bc("Work centers", 99000755, "on"), cm("Expiry", "skills-matrix", "future", "has")], "Trainee → qualified → trainer, per operation"),
      cm("Training assignments", "training-records", "future", "is assigned", [
        cm("Courses", "training-records", "future", "is for"),
        cm("Completion records", "training-records", "future", "is completed as"),
      ], "From a role, a new document revision, or a supervisor"),
      cm("Acknowledgements", "acknowledgements", "r1", "signs"),
    ]),
    cm("Roles", "skills-matrix", "future", undefined, [cm("Required training", "skills-matrix", "future", "requires")]),
  ],
  improve: [
    cm("Suggestions", "suggestions", "future", undefined, [
      cm("Kaizen projects", "kaizen", "future", "becomes", undefined, "When the weekly review approves it"),
    ], "From anyone on the floor, or an improvement-idea issue"),
    cm("Kaizen projects", "kaizen", "future", undefined, [
      cm("A3", "kaizen", "future", "is documented in"),
      cm("Actions", "kaizen", "future", "has"),
      cm("Savings", "kaizen", "future", "is measured by", [bc("Item and work center costs", 31, "verified against")], "Verified against BC costs"),
    ]),
    cm("5S audits", "5s", "future", undefined, [cm("Findings", "5s", "future", "has", [cm("Actions", "kaizen", "future", "results in")])]),
  ],
  requests: [
    cm("Request types", "approvals", "future", undefined, [
      cm("Requests", "approvals", "future", "is submitted as", [
        cm("Approval steps", "approvals", "future", "moves through", undefined, "Approve from Teams"),
        bc("Purchase orders", 9307, "a purchase request creates"),
        bc("Fixed assets", 5601, "a capex request creates"),
      ]),
    ], "Capex, purchase, deviation, travel… each with its own form and approvers"),
    cm("Deviations", "deviations", "future", undefined, [bc("Items", 31, "applies to"), cm("Expiry", "deviations", "future", "has")], "Temporary permission to run outside spec"),
  ],
  finance: [
    bc("Chart of accounts", 16, undefined, [bc("General journals", 39, "is posted by"), bc("General ledger entries", 20, "records")]),
    bc("Customers", 22, undefined, [bc("Posted sales invoices", 143, "is invoiced"), bc("Cash receipt journals", 255, "pays through"), bc("Customer ledger entries", 25, "owes")]),
    bc("Vendors", 27, undefined, [bc("Posted purchase invoices", 146, "invoices"), bc("Payment journals", 256, "is paid through"), bc("Vendor ledger entries", 29, "is owed")]),
    bc("Bank accounts", 371),
    bc("Fixed assets", 5601),
  ],
};

// ---------- Main records per area, each with its related records in three groups ----------
// Related: reference records that rarely change. Active: open work in flight. History: closed and posted.
export type Anchor = {
  object: string; // ontology object type id
  node: ModelNode; // the record itself
  related: ModelNode[];
  active: ModelNode[];
  history: ModelNode[];
};

const A = (object: string, node: ModelNode, related: ModelNode[], active: ModelNode[], history: ModelNode[]): Anchor => ({ object, node, related, active, history });
const issuesOpen = cm("Open issues", "issues", "live", undefined, undefined, undefined, "/issues");
const issuesClosed = cm("Closed issues and CAPAs", "issues", "live", undefined, undefined, undefined, "/issues?state=closed");

export const ANCHORS: Record<string, Anchor[]> = {
  commercial: [
    A("account", cm("Accounts", "accounts", "live", undefined, undefined, "One account rolls up its BC customers", "/accounts"),
      [cm("Contacts", "accounts", "live", undefined, undefined, undefined, "/accounts"), bc("Ship-to addresses", 301), bc("Customers (sell-to, bill-to)", 22), cm("Account team", "accounts", "r3")],
      [cm("Opportunities", "crm", "live", undefined, undefined, undefined, "/opportunities"), cm("Quotes", "quotes", "live", undefined, undefined, undefined, "/opportunities"), bc("Sales quotes", 9300), bc("Sales orders", 9305), cm("Cases", "cases", "r3"), bc("Open receivables", 25)],
      [cm("Won and lost opportunities", "crm", "live", undefined, undefined, undefined, "/opportunities?view=list"), bc("Posted shipments", 142), bc("Posted invoices", 143), bc("Posted credit memos", 144), cm("Closed cases and complaints", "cases", "r3")]),
    A("lead", cm("Leads", "leads", "r3"),
      [bc("Contact", 5052), cm("Source and campaign", "leads", "r3")],
      [cm("Open leads", "leads", "r3"), cm("Activities", "crm", "r3")],
      [cm("Converted leads", "leads", "r3", undefined, undefined, "With the account and opportunity they became"), cm("Disqualified leads", "leads", "r3")]),
  ],
  engineering: [
    A("item", bc("Items", 31),
      [bc("Production BOM", 99000787), bc("Routing", 99000764), bc("Item variants", 5401), cm("Revisions", "new-items", "r2"), cm("Drawings and specs", "doc-control", "r1"), bc("Vendors who supply it", 114)],
      [cm("Open change requests and ECOs", "eco", "r2"), cm("New item request", "new-items", "r2"), bc("Open production orders", 9326), bc("Open purchase orders", 9307), cm("Holds", "holds", "future"), issuesOpen],
      [bc("Item ledger entries", 38), cm("Released ECOs", "eco", "r2"), issuesClosed]),
  ],
  quality: [
    A("lot", bc("Lots", 6508, undefined, undefined, "The hub between the floor, suppliers and customers"),
      [bc("Item", 31), bc("Vendor", 27), bc("Location", 15)],
      [cm("Holds", "holds", "future"), issuesOpen, cm("Open nonconformances", "ncr", "future"), cm("Open CAPAs", "capa", "live", undefined, undefined, undefined, "/capas")],
      [bc("Item tracing", 6520), bc("Item ledger entries", 38), cm("Inspection results", "inspection", "future"), issuesClosed]),
    A("capa", cm("CAPAs", "capa", "live", undefined, undefined, undefined, "/capas"),
      [cm("Source issues", "issues", "live", undefined, undefined, undefined, "/issues"), bc("Lots", 6508), bc("Vendor", 27), cm("Procedures it revises", "doc-control", "r1")],
      [cm("Containment and corrective actions", "capa", "live", undefined, undefined, undefined, "/capas"), cm("SCAR", "scar", "r1"), cm("Change request", "eco", "r2")],
      [cm("Effectiveness checks", "capa", "live", undefined, undefined, undefined, "/capas"), cm("Closed CAPAs", "capa", "live", undefined, undefined, undefined, "/capas")]),
  ],
  supply: [
    A("vendor", bc("Vendors", 27),
      [bc("Contacts", 5052), cm("Approved supplier status", "supplier-portal", "r1"), bc("Items supplied", 114), cm("Portal users", "supplier-portal", "r1")],
      [bc("Open purchase orders", 9307), cm("Acknowledgements due", "supplier-portal", "live", undefined, undefined, undefined, "/supplier-portal?view=awaiting"), cm("Open SCARs", "scar", "r2"), bc("Open payables", 29), issuesOpen],
      [bc("Posted receipts", 145), bc("Posted purchase invoices", 146), cm("Closed SCARs", "scar", "r1"), cm("Scorecards", "scorecards", "r1")]),
  ],
  production: [
    A("work_center", bc("Work centers", 99000755),
      [bc("Machine centers", 99000760), bc("Routings that use it", 99000764), cm("Qualified operators", "skills-matrix", "future"), cm("Work instructions", "doc-control", "r1")],
      [bc("Released production orders", 9326), issuesOpen, cm("Downtime", "downtime", "future"), cm("Tier actions", "tier-boards", "r1")],
      [bc("Capacity ledger entries", 5832), issuesClosed, cm("Shift handoffs", "shop-floor", "future")]),
    A("production_order", bc("Production orders", 9326),
      [bc("Item", 31), bc("Routing", 99000764), bc("Production BOM", 99000787)],
      [cm("Operator jobs", "shop-floor", "future"), cm("Schedule", "scheduling", "future"), issuesOpen],
      [bc("Output and consumption (item ledger)", 38), bc("Lots produced", 6508), issuesClosed]),
  ],
  inventory: [
    A("item", bc("Items", 31),
      [bc("Locations", 15), bc("Bins", 7303), bc("Lots", 6508)],
      [cm("Holds", "holds", "future"), bc("Warehouse receipts", 7332), bc("Warehouse shipments", 7339), bc("Transfer orders", 5742), cm("Counts", "cycle-counts", "live", undefined, undefined, undefined, "/inventory/counts")],
      [bc("Item ledger entries", 38), bc("Physical inventory ledger", 390), cm("Released holds", "holds", "future")]),
  ],
  maintenance: [
    A("asset", cm("Assets", "assets", "live"),
      [bc("Machine center", 99000760), bc("Fixed asset", 5601), cm("PM schedules", "pm", "live"), cm("Critical spares", "spares", "live"), cm("Manuals", "doc-control", "r1")],
      [cm("Open work orders", "work-orders", "live"), cm("PMs due", "pm", "live"), cm("Downtime", "downtime", "future")],
      [cm("Completed work orders", "work-orders", "live"), cm("Meter readings", "pm", "live"), bc("Fixed asset ledger entries", 5604)]),
  ],
  safety: [
    A("incident", cm("Incidents", "incidents", "future"),
      [bc("Employees involved", 5201), bc("Location", 15), cm("Chemicals and SDS", "environmental", "future")],
      [cm("Investigations", "incidents", "future"), cm("Open corrective actions", "incidents", "future")],
      [cm("Closed incidents", "incidents", "future"), cm("OSHA 300 log", "incidents", "future")]),
  ],
  documents: [
    A("document", cm("Controlled documents", "doc-control", "r1"),
      [bc("Items", 31), bc("Work centers", 99000755), cm("Related documents", "doc-control", "r1")],
      [cm("Draft revision", "doc-control", "r1"), cm("Change requests", "doc-control", "r1"), cm("Approvals pending", "doc-control", "r1"), cm("Acknowledgements outstanding", "acknowledgements", "r1")],
      [cm("Past revisions", "doc-control", "r1"), cm("Completed records", "doc-control", "r1")]),
  ],
  training: [
    A("employee", bc("Employees", 5201),
      [cm("Role", "skills-matrix", "future"), cm("Qualifications", "skills-matrix", "future"), bc("Work centers", 99000755)],
      [cm("Training assignments", "training-records", "future"), cm("Qualifications expiring", "skills-matrix", "future"), cm("Acknowledgements due", "acknowledgements", "r1")],
      [cm("Completion records", "training-records", "future"), cm("Acknowledgements signed", "acknowledgements", "r1"), cm("Incidents", "incidents", "future")]),
  ],
  improve: [
    A("kaizen", cm("Kaizen projects", "kaizen", "future"),
      [bc("Work centers", 99000755), cm("Issues it came from", "issues", "live", undefined, undefined, undefined, "/issues")],
      [cm("A3", "kaizen", "future"), cm("Open actions", "kaizen", "future")],
      [cm("Verified savings", "kaizen", "future"), cm("Completed projects", "kaizen", "future")]),
  ],
  requests: [
    A("request", cm("Requests", "approvals", "future"),
      [cm("Requester and approvers", "approvals", "future"), cm("Request type", "approvals", "future")],
      [cm("Approval steps", "approvals", "future"), cm("Open deviations", "deviations", "future")],
      [cm("Approved and rejected", "approvals", "future"), bc("BC records created", 9307)]),
  ],
  finance: [
    A("customer", bc("Customers", 22),
      [bc("Contacts", 5052), bc("Ship-to addresses", 301)],
      [bc("Open receivables", 25), bc("Unposted sales invoices", 9301)],
      [bc("Posted invoices", 143), bc("Posted credit memos", 144)]),
    A("vendor", bc("Vendors", 27),
      [bc("Contacts", 5052), bc("Items supplied", 114)],
      [bc("Open payables", 29), bc("Unposted purchase invoices", 9308)],
      [bc("Posted purchase invoices", 146), bc("Posted purchase credit memos", 147)]),
  ],
};

export const anchorFor = (objectId: string) => {
  for (const list of Object.values(ANCHORS)) for (const a of list) if (a.object === objectId) return a;
  return null;
};
